
import { defineComponent, nextTick, onBeforeMount, onBeforeUnmount, reactive, toRaw } from 'vue'

// other packages
import moment from 'moment'

// components
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// module components
import Orders from '@/modules/DispatcherModule/components/Orders/index.vue'
import SubmitOrders from '@/modules/DispatcherModule/components/SubmitOrders/index.vue'
import SubmitOrder from '@/modules/DispatcherModule/components/SubmitOrder/index.vue'
import SubmitCheckedOrders from '@/modules/DispatcherModule/components/SubmitCheckedOrders/index.vue'
import ChangeDate from '@/modules/DispatcherModule/components/ChangeDate/index.vue'

// realisation
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'
import { sectors } from '@/core/realisations/sectors.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    DispatcherDeliveryListOrderT,
    DispatcherTakeOrderCardT,
    DispatcherTakeOrderT,
} from '@/core/types/dispatcher.types'
import { SectorT } from '@/core/types/sectors.types'

// composable
import useCatchError from '@/common/composable/useCatchError'
import useDispatcherMap from '@/modules/DispatcherModule/composable/useDispatcherMap'
import useHelpers from '@/common/composable/useHelpers'
import useAccount from '@/common/composable/useAccount'

// constants
const KASPI_ID = 114
import { DispatcherSectorT } from '@/core/types/dispatchers_sector.types'

// event bus
import emitter from 'tiny-emitter/instance'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'the-select': TheSelect,
        'the-input': TheInput,
        checkbox: Checkbox,
        orders: Orders,
        'submit-order': SubmitOrder,
        'submit-orders': SubmitOrders,
        'submit-checked-orders': SubmitCheckedOrders,
        'change-date': ChangeDate,
        loader: Loader,
    },
    setup() {
        const notifications = useNotifications()
        const now_date = moment().format('YYYY-MM-DD')
        const account = useAccount()
        const user_id = account.state.user_id

        const state = reactive({
            dispatcher_sector_id: null as number | null,
            order_number_1c: null as string | null,
            selected_order_id: null as number | null,
            is_submiting_orders: false,
            is_submiting_order: false,
            is_submiting_checked_orders: false,
            exclude_companies_ids: [] as number[],
            wait_list_status_ids: [] as number[],
            company_ids: [] as number[],
            status_ids: [1, 2] as number[],
            is_delivery_sectors_showed: false,
            is_changing_date: false,
            from_date: now_date as string | null,
            to_date: now_date as string | null,
            selected_orders_ids: [] as number[],
            auto_assign_enable: false,
            is_auto_assign_loading: false,
            is_load_deliveries: true,
        })

        const take_orders = reactive(new DataState<DispatcherTakeOrderT>({}))
        const delivery_orders = reactive(new DataState<DispatcherDeliveryListOrderT>({}))

        const dispatcherSectors = reactive(new DataState<DispatcherSectorT>({}))
        const deliverySectors = reactive(new DataState<SectorT>({}))

        const dispatcher_map = useDispatcherMap()

        const onCheckOrder = (id: number) => {
            const index = state.selected_orders_ids.findIndex((order_id) => order_id === id)

            if (index < 0) {
                state.selected_orders_ids.push(id)
            } else {
                state.selected_orders_ids.splice(index, 1)
            }
        }

        const loadTakeOrders = async () => {
            const { dispatcher_sector_id, from_date, to_date } = toRaw(state)
            if (!dispatcher_sector_id || !from_date || !to_date) return

            dispatcher_map.removeClusters()
            state.selected_orders_ids = []

            try {
                take_orders.is_loading = true

                const response = await dispatcher.getTakeOrders(dispatcher_sector_id, {
                    from_date,
                    to_date,
                    inside_polygon: 1,
                    exclude_companies_ids: state.exclude_companies_ids,
                    wait_list_status_ids: state.wait_list_status_ids,
                    company_ids: state.company_ids,
                    status_ids: state.status_ids,
                    order_number_1c: state.order_number_1c,
                })

                take_orders.list = response.data

                if (isTakesEnabled()) {
                    dispatcher_map.renderOrders(take_orders.list)
                }

                take_orders.is_failed = false
                take_orders.failure.error_state.is_cancel = false
            } catch (error) {
                take_orders.failure.catchError(error)

                if (!take_orders.failure.error_state.is_cancel) {
                    take_orders.is_failed = true
                }
            } finally {
                if (!take_orders.failure.error_state.is_cancel) {
                    take_orders.is_loading = false
                }
            }
        }

        const loadDeliveryOrders = async () => {
            delivery_orders.list = []
            if (!state.is_load_deliveries) return

            const { dispatcher_sector_id, from_date, to_date } = toRaw(state)
            if (!dispatcher_sector_id || !from_date || !to_date) return

            try {
                delivery_orders.is_loading = true

                // @ts-ignore
                const response = await dispatcher.getDeliveryList({
                    dispatcher_sector_id,
                    deliveryStatus: 2,
                    limit: 9999,
                })

                // const response = await dispatcher.getTakeOrders(dispatcher_sector_id, {
                //     from_date,
                //     to_date,
                //     inside_polygon: 1,
                //     exclude_companies_ids: state.exclude_companies_ids,
                //     company_ids: state.company_ids,
                //     status_ids: state.status_ids,
                //     order_number_1c: state.order_number_1c,
                // })

                delivery_orders.list = response.data
                dispatcher_map.renderDeliveryOrders(delivery_orders.list)

                delivery_orders.is_failed = false
                delivery_orders.failure.error_state.is_cancel = false
            } catch (error) {
                delivery_orders.failure.catchError(error)
                notifications.notifyError({
                    title: 'При загрузке доставок произошла ошибка',
                    error,
                })

                if (!delivery_orders.failure.error_state.is_cancel) {
                    delivery_orders.is_failed = true
                }
            } finally {
                if (!delivery_orders.failure.error_state.is_cancel) {
                    delivery_orders.is_loading = false
                }
            }
        }

        const reloadOrders = () => {
            loadTakeOrders()
            loadDeliveryOrders()
        }

        const loadSectors = async () => {
            try {
                deliverySectors.is_loading = true

                const response = await sectors.getList()
                deliverySectors.list = response.data

                deliverySectors.is_failed = false
                deliverySectors.failure.error_state.is_cancel = false
            } catch (error) {
                deliverySectors.failure.catchError(error)

                if (!deliverySectors.failure.error_state.is_cancel) {
                    deliverySectors.is_failed = true
                }
            } finally {
                if (!deliverySectors.failure.error_state.is_cancel) {
                    deliverySectors.is_loading = false
                }
            }
        }

        const loadTakeOrdersAndSectors = async (id: number) => {
            dispatcher_map.removeAllGeoObjects()
            const dispatcher_sector = dispatcherSectors.list.find((ds) => ds.id === id)

            if (dispatcher_sector) {
                const coordinates = JSON.parse(dispatcher_sector.coordinates)
                const centroid = dispatcher_map.getPolygonCentroid(coordinates)
                dispatcher_map.setMapCenter(centroid)
                state.auto_assign_enable = dispatcher_sector.auto_assign_enable
            }

            await loadSectors()

            if (state.is_delivery_sectors_showed) {
                dispatcher_map.drawSectors(deliverySectors.list)
            }

            state.exclude_companies_ids = []
            state.wait_list_status_ids = []
            state.company_ids = []
            if (state.dispatcher_sector_id === 24) {
                // HARDCODE SP-1468
                // IF ALMATY, THEN PUSH KASPI TO EXCLUDE
                state.exclude_companies_ids.push(KASPI_ID)
            }

            loadTakeOrders()
            loadDeliveryOrders()
        }

        const selected_order_state = reactive({
            is_loading: false,
            is_failed: false,
            card: null as DispatcherTakeOrderCardT | null,
        })

        const selected_order_error_state = useCatchError()

        const loadTakeOrder = async (order_id: number) => {
            try {
                selected_order_state.is_loading = true

                const response = await dispatcher.getTakeOrder(order_id)
                selected_order_state.card = response.data

                selected_order_state.is_failed = false
            } catch (error) {
                selected_order_error_state.catchError(error)

                if (!selected_order_error_state.error_state.is_cancel) {
                    selected_order_state.is_failed = true
                }
            } finally {
                if (!selected_order_error_state.error_state.is_cancel) {
                    selected_order_state.is_loading = false
                }
            }
        }

        const setSelectedOrder = (order_id: number) => {
            if (state.selected_order_id === order_id) {
                return
            }

            state.selected_order_id = order_id
            loadTakeOrder(order_id)
        }

        const startSubmitOrders = () => {
            state.is_submiting_orders = true
        }

        const cancelSubmitOrders = () => {
            state.is_submiting_orders = false
        }

        const startSubmitOrder = () => {
            state.is_submiting_order = true
        }

        const cancelSubmitOrder = () => {
            state.is_submiting_order = false
        }

        const startSubmitCheckedOrders = () => {
            state.is_submiting_checked_orders = true
        }

        const cancelSubmitCheckedOrders = () => {
            state.is_submiting_checked_orders = false
        }

        const loadDispatcherSectors = async () => {
            try {
                dispatcherSectors.is_loading = true

                const response = await dispatchers_sectors.getList()
                dispatcherSectors.list = response.data

                dispatcherSectors.is_completed = true
                dispatcherSectors.is_failed = false
                dispatcherSectors.failure.error_state.is_cancel = false
            } catch (error) {
                dispatcherSectors.failure.catchError(error)

                if (!dispatcherSectors.failure.error_state.is_cancel) {
                    dispatcherSectors.is_failed = true
                }
            } finally {
                if (!dispatcherSectors.failure.error_state.is_cancel) {
                    dispatcherSectors.is_loading = false
                }
            }
        }

        onBeforeMount(async () => {
            await loadDispatcherSectors()

            // Автозаполнение сектора
            if (dispatcherSectors.list) {
                let auto_complete_sector = dispatcherSectors.list.find(
                    (s) => s.users && s.users.find((user) => user.id === user_id)
                )
                if (auto_complete_sector) {
                    state.dispatcher_sector_id = auto_complete_sector.id
                    loadTakeOrdersAndSectors(state.dispatcher_sector_id)
                }
            }

            if (dispatcher_map.isMapInstanceDefined.value) {
                dispatcher_map.drawSectors(dispatcherSectors.list)
            } else {
                emitter.on('DISPATCHER_MAP_READY', async () => {
                    dispatcher_map.drawSectors(dispatcherSectors.list)
                    emitter.off('DISPATCHER_MAP_READY')
                })
            }

            emitter.on('DISPATCHER_ORDER_CHANGE_DATE', (order_id: number) => {
                setSelectedOrder(order_id)
                state.is_changing_date = true
            })

            emitter.on('DISPATCHER_ORDER_GIVE_TO_COURIER', (order_id: number) => {
                setSelectedOrder(order_id)
                state.is_submiting_order = true
            })
        })

        onBeforeUnmount(() => {
            dispatcher_map.removeAllGeoObjects()

            emitter.off('DISPATCHER_ORDER_CHANGE_DATE')
            emitter.off('DISPATCHER_ORDER_GIVE_TO_COURIER')
        })

        const updateExcludedCompanies = (companies_ids: number[]) => {
            state.exclude_companies_ids = companies_ids
            loadTakeOrders()

            if (state.is_load_deliveries) {
                dispatcher_map.renderDeliveryOrders(delivery_orders.list)
            }
        }

        const updateWaitListStatusIds = (wait_list_status_ids: number[]) => {
            state.wait_list_status_ids = wait_list_status_ids
            loadTakeOrders()

            if (state.is_load_deliveries) {
                dispatcher_map.renderDeliveryOrders(delivery_orders.list)
            }
        }

        const updateIncludedCompanies = (company_ids: number[]) => {
            state.company_ids = company_ids
            loadTakeOrders()

            if (state.is_load_deliveries) {
                dispatcher_map.renderDeliveryOrders(delivery_orders.list)
            }
        }

        const updateStatuses = (status_id: number, is_checked: boolean) => {
            const index = state.status_ids.findIndex((statusId) => statusId === status_id)

            if (index < 0) {
                if (is_checked) {
                    state.status_ids.push(status_id)
                }
            } else {
                if (!is_checked) {
                    state.status_ids.splice(index, 1)
                }
            }

            loadTakeOrders()

            if (state.is_load_deliveries) {
                dispatcher_map.renderDeliveryOrders(delivery_orders.list)
            }
        }

        const isTakesEnabled = (): boolean => {
            return state.status_ids.includes(1) || state.status_ids.includes(2)
        }

        const updateIsLoadDeliveries = (value: boolean) => {
            if (!value) {
                if (delivery_orders.list.length) {
                    dispatcher_map.removeClusters()

                    if (isTakesEnabled()) {
                        dispatcher_map.renderOrders(take_orders.list)
                    }
                }

                return
            } else {
                if (delivery_orders.list.length) {
                    dispatcher_map.renderDeliveryOrders(delivery_orders.list)
                } else {
                    loadDeliveryOrders()
                }
            }
        }

        const searchBy1C = (order_number_1c: string) => {
            state.order_number_1c = order_number_1c
            loadTakeOrders()

            if (state.is_load_deliveries) {
                dispatcher_map.renderDeliveryOrders(delivery_orders.list)
            }
        }

        const onToggleShowDeliverySectors = () => {
            if (state.is_delivery_sectors_showed) {
                dispatcher_map.drawSectors(deliverySectors.list)
            } else {
                dispatcher_map.removeAllPolygons()
            }
        }

        const onDateChanged = () => {
            state.is_changing_date = false

            selected_order_state.card = null
            selected_order_state.is_loading = false
            selected_order_state.is_failed = false

            if (!state.selected_order_id) return

            const { findItemIndexById } = useHelpers()
            const order_index = findItemIndexById(state.selected_order_id, take_orders.list)
            take_orders.list.splice(order_index, 1)

            dispatcher_map.removeClusters()

            if (isTakesEnabled()) {
                dispatcher_map.renderOrders(take_orders.list)
            }

            if (state.is_load_deliveries) {
                dispatcher_map.renderDeliveryOrders(delivery_orders.list)
            }

            state.selected_order_id = null
        }

        const onOrderSubmited = async () => {
            if (!state.selected_order_id) return

            await loadTakeOrders()

            if (state.is_load_deliveries) {
                dispatcher_map.renderDeliveryOrders(delivery_orders.list)
            }

            await loadTakeOrder(state.selected_order_id)

            cancelSubmitOrder()
        }

        const updateAutoAssignInSector = (value: boolean, dispatcher_sector_id: number) => {
            const dispatcher_sector = dispatcherSectors.list.find(
                (ds) => ds.id === dispatcher_sector_id
            )
            if (dispatcher_sector) {
                dispatcher_sector.auto_assign_enable = value
            }
        }

        const toggleAutoAssign = async (is_enable: boolean) => {
            if (!state.dispatcher_sector_id || state.is_auto_assign_loading) return

            const enable_question =
                'Вы действительно хотите включить автоназначение заборов для этого сектора ?'
            const disable_question =
                'Вы действительно хотите выключить автоназначение заборов для этого сектора ?'

            const question = is_enable ? enable_question : disable_question
            const answer = confirm(question)

            if (!answer) {
                await nextTick()

                if (is_enable) {
                    state.auto_assign_enable = false
                } else {
                    state.auto_assign_enable = true
                }

                return
            }

            try {
                state.is_auto_assign_loading = true

                const date = moment().format('YYYY-MM-DD')

                if (is_enable) {
                    await dispatcher.enableAutoAssign({
                        date,
                        dispatchersSectorId: state.dispatcher_sector_id,
                    })

                    state.auto_assign_enable = true
                    updateAutoAssignInSector(true, state.dispatcher_sector_id)
                } else {
                    await dispatcher.disableAutoAssign({
                        date,
                        dispatchersSectorId: state.dispatcher_sector_id,
                    })

                    state.auto_assign_enable = false
                    updateAutoAssignInSector(false, state.dispatcher_sector_id)
                }
            } catch (error) {
                const enable_error_title = 'При включении автоназначения произошла ошибка'
                const disable_error_title = 'При выключении автоназначения произошла ошибка'

                const title = is_enable ? enable_error_title : disable_error_title
                notifications.notifyError({ error, title })

                if (is_enable) {
                    state.auto_assign_enable = false
                } else {
                    state.auto_assign_enable = true
                }
            } finally {
                state.is_auto_assign_loading = false
            }
        }

        return {
            state,
            dispatcherSectors,
            take_orders,
            loadDispatcherSectors,
            loadTakeOrders,
            reloadOrders,
            selected_order_state,
            selected_order_error_state,
            loadTakeOrdersAndSectors,
            loadTakeOrder,
            setSelectedOrder,
            startSubmitOrders,
            cancelSubmitOrders,
            startSubmitOrder,
            cancelSubmitOrder,
            updateExcludedCompanies,
            updateWaitListStatusIds,
            updateIncludedCompanies,
            updateStatuses,
            updateIsLoadDeliveries,
            searchBy1C,
            onToggleShowDeliverySectors,
            onDateChanged,
            onOrderSubmited,
            onCheckOrder,
            startSubmitCheckedOrders,
            cancelSubmitCheckedOrders,
            toggleAutoAssign,
        }
    },
})
